import { useState, useEffect, useRef } from "react";
import {
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  FormGroup, // Added for better layout
} from "reactstrap";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { createProgramNew } from "../../../../store/slices/programsSlice";
import {
  getAllClients,
  getAllCountries,
  getAddOnMaster,
} from "../../../../store/slices/clientsSlice";
import { getAllMembershipPlans } from "../../../../store/slices/membershipPlansSlice";
import Select from "react-select";
import { toast } from "react-toastify";
import MbForm from "./mbform";
import allAPI from "../../../../api/api";

const CreateProgramModal = ({ isOpen, toggle, pagination }) => {
  const clientsList = useSelector((state) => state.clients.clients || []);
  const countries = useSelector((state) => state.clients.allCountries || []);
  const selectedTenant = useSelector(
    (state) => state.clients.selectedClient?.value,
  );
  const membershipPlans = useSelector(
    (state) => state.membershipPlans.membershipPlans || [],
  );
  const masterAddOns = useSelector((state) => state.clients.masterAddOns);

  const [addOnOptions, setAddOnOptions] = useState([]);
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [newDetailText, setNewDetailText] = useState("");
  const [subDetailInputs, setSubDetailInputs] = useState({});
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [newProgramDetailText, setNewProgramDetailText] = useState("");
  const [programSubDetailInputs, setProgramSubDetailInputs] = useState({});
  const [programDetailsOpen, setProgramDetailsOpen] = useState(true);
  const [programDetailUploading, setProgramDetailUploading] = useState({});
  const [syncWithPlCrm, setSyncWithPlCrm] = useState(false);

  const clientOptions = Array.isArray(clientsList)
    ? clientsList.map((client) => ({
        label: client.client_name,
        value: client.ID,
      }))
    : [];

  const membershipOptions = Array.isArray(membershipPlans)
    ? membershipPlans.map((mp) => ({ label: mp.name, value: mp.name }))
    : [];

  const dispatch = useDispatch();

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllClients());
      dispatch(getAllCountries());
      dispatch(getAllMembershipPlans());
      dispatch(getAddOnMaster({ isMaster: true }));
    }
  }, [isOpen, dispatch]);

  const [formFields, setFormFields] = useState({
    name: "",
    slug_name: "",
    country: "",
    client_id: "",
    membership_plan: "",
    description: "",
    registration_type: "",
    bin_numbers: [{ from: "", to: "" }],
    mb_form: {},
    // --- New Hierarchy Fields ---
    plan: true, // Default true
    service: false,
    subService: false,
    category: false,
    subCategory: false,
    add_details: false,
    additional_details: [],
    program_details: [],
    add_on_services: [],
  });
  const slugManuallyEdited = useRef(false);

  const {
    register,
    trigger,
    formState: { errors },
    setValue,
  } = useForm({ defaultValues: { name: "", description: "" } });

  const [formFieldsValidation, setFormFieldsValidation] = useState({
    role_value: {
      ...register("name", { required: "This field is required" }),
    },
    description: {
      ...register("description", { required: "This field is required" }),
    },
  });

  // Derive add-on master options from redux store
  useEffect(() => {
    const derive = (arr) =>
      Array.isArray(arr)
        ? arr.map((it) => {
            if (typeof it === "string") return { label: it, value: it };
            const label =
              it.serviceName ?? it.name ?? it.label ?? it.title ?? String(it);
            const value =
              it.ID ??
              it.id ??
              it.value ??
              it.limitsValue ??
              it.limits ??
              label;
            return { label, value };
          })
        : [];
    const opts = derive(
      Array.isArray(masterAddOns) && masterAddOns.length > 0
        ? masterAddOns
        : [],
    );
    setAddOnOptions(opts);
    if (!selectedMaster && opts.length > 0) setSelectedMaster(opts[0]);
  }, [masterAddOns]);

  const emptyAddon = () => ({
    limits: "",
    limitsValue: "",
    default_free: "",
    extraFree: "",
    purchase_slot: "",
    total_slot: "",
    isEnabled: true,
  });

  const addAddon = () => {
    const masterSource =
      Array.isArray(masterAddOns) && masterAddOns.length > 0
        ? masterAddOns
        : [];
    const masterRecord = selectedMaster
      ? masterSource.find((m) => (m.ID ?? m.id) === selectedMaster.value) ||
        null
      : null;
    const newRow = masterRecord
      ? {
          limits:
            masterRecord.serviceName ??
            masterRecord.name ??
            selectedMaster.label ??
            "",
          limitsValue:
            masterRecord.ID ?? masterRecord.id ?? selectedMaster.value ?? "",
          default_free:
            masterRecord.defaultFree != null
              ? String(masterRecord.defaultFree)
              : "",
          extraFree: "",
          purchase_slot:
            masterRecord.purchaseSLot != null
              ? String(masterRecord.purchaseSLot)
              : "",
          total_slot:
            masterRecord.maxSlot != null ? String(masterRecord.maxSlot) : "",
          isEnabled:
            masterRecord.isEnable !== undefined
              ? !!masterRecord.isEnable
              : true,
          personal_identity: !!(
            masterRecord.personalIdentity ??
            masterRecord.personal_identity ??
            true
          ),
          financial_identity: !!(
            masterRecord.financialIdentity ??
            masterRecord.financial_identity ??
            false
          ),
          digital_itendity: !!(
            masterRecord.digitalItendity ??
            masterRecord.digital_itendity ??
            false
          ),
          serviceKey:
            masterRecord.serviceKey ??
            masterRecord.service_key ??
            masterRecord?.serviceName ??
            masterRecord?.name ??
            "",
        }
      : {
          ...emptyAddon(),
          limitsValue: selectedMaster?.value ?? addOnOptions?.[0]?.value ?? "",
          limits: selectedMaster?.label ?? addOnOptions?.[0]?.label ?? "",
        };
    setFormFields((prev) => ({
      ...prev,
      add_on_services: [...prev.add_on_services, newRow],
    }));
  };

  const removeAddon = (index) =>
    setFormFields((prev) => ({
      ...prev,
      add_on_services: prev.add_on_services.filter((_, i) => i !== index),
    }));

  const updateAddon = (index, field, value) =>
    setFormFields((prev) => ({
      ...prev,
      add_on_services: prev.add_on_services.map((row, i) =>
        i === index ? { ...row, [field]: value } : row,
      ),
    }));

  const addDetail = () => {
    const text = newDetailText.trim();
    if (!text) return;
    setFormFields((prev) => ({
      ...prev,
      add_details: true,
      additional_details: [
        ...prev.additional_details,
        { text, subDetails: [] },
      ],
    }));
    setNewDetailText("");
  };

  const removeDetail = (index) => {
    setFormFields((prev) => ({
      ...prev,
      additional_details: prev.additional_details.filter((_, i) => i !== index),
    }));
    setSubDetailInputs((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const addSubDetail = (detailIndex) => {
    const text = (subDetailInputs[detailIndex] || "").trim();
    if (!text) return;
    setFormFields((prev) => ({
      ...prev,
      additional_details: prev.additional_details.map((d, i) =>
        i === detailIndex
          ? { ...d, subDetails: [...(d.subDetails || []), { text }] }
          : d,
      ),
    }));
    setSubDetailInputs((prev) => ({ ...prev, [detailIndex]: "" }));
  };

  const removeSubDetail = (detailIndex, subIndex) => {
    setFormFields((prev) => ({
      ...prev,
      additional_details: prev.additional_details.map((d, i) =>
        i === detailIndex
          ? {
              ...d,
              subDetails: (d.subDetails || []).filter(
                (_, si) => si !== subIndex,
              ),
            }
          : d,
      ),
    }));
  };

  // --- Program Details (with optional image) ---
  const addProgramDetail = () => {
    const text = newProgramDetailText.trim();
    if (!text) return;
    setFormFields((prev) => ({
      ...prev,
      program_details: [
        ...(prev.program_details || []),
        { text, subDetails: [], image_url: "" },
      ],
    }));
    setNewProgramDetailText("");
  };

  const removeProgramDetail = (index) => {
    setFormFields((prev) => ({
      ...prev,
      program_details: (prev.program_details || []).filter(
        (_, i) => i !== index,
      ),
    }));
    setProgramSubDetailInputs((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setProgramDetailUploading((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const addProgramSubDetail = (detailIndex) => {
    const text = (programSubDetailInputs[detailIndex] || "").trim();
    if (!text) return;
    setFormFields((prev) => ({
      ...prev,
      program_details: (prev.program_details || []).map((d, i) =>
        i === detailIndex
          ? { ...d, subDetails: [...(d.subDetails || []), { text }] }
          : d,
      ),
    }));
    setProgramSubDetailInputs((prev) => ({ ...prev, [detailIndex]: "" }));
  };

  const removeProgramSubDetail = (detailIndex, subIndex) => {
    setFormFields((prev) => ({
      ...prev,
      program_details: (prev.program_details || []).map((d, i) =>
        i === detailIndex
          ? {
              ...d,
              subDetails: (d.subDetails || []).filter(
                (_, si) => si !== subIndex,
              ),
            }
          : d,
      ),
    }));
  };

  const uploadProgramDetailImage = async (index, file) => {
    if (!file) return;
    setProgramDetailUploading((prev) => ({ ...prev, [index]: true }));
    try {
      const fd = new FormData();
      fd.append("image", file);
      const mediaRes = await allAPI.clients.uploadMedia(fd);
      const path =
        mediaRes?.data?.data?.image?.[0]?.path ||
        mediaRes?.data?.filename ||
        mediaRes?.data?.fileName ||
        mediaRes?.data?.id ||
        mediaRes?.data;
      if (path) {
        setFormFields((prev) => ({
          ...prev,
          program_details: (prev.program_details || []).map((d, i) =>
            i === index ? { ...d, image_url: path } : d,
          ),
        }));
      }
    } catch (err) {
      console.error("Failed to upload program detail image:", err);
    } finally {
      setProgramDetailUploading((prev) => ({ ...prev, [index]: false }));
    }
  };

  const removeProgramDetailImage = (index) => {
    setFormFields((prev) => ({
      ...prev,
      program_details: (prev.program_details || []).map((d, i) =>
        i === index ? { ...d, image_url: "" } : d,
      ),
    }));
  };

  // --- Logic to handle Hierarchy Toggles ---
  const handleHierarchyChange = (e) => {
    const { name, checked } = e.target;

    setFormFields((prev) => {
      let updated = { ...prev, [name]: checked };

      // If unchecking a parent, uncheck all its children
      if (name === "plan" && !checked) {
        updated.service = false;
        updated.subService = false;
        updated.category = false;
        updated.subCategory = false;
      }
      if (name === "service" && !checked) {
        updated.subService = false;
        updated.category = false;
        updated.subCategory = false;
      }
      if (name === "subService" && !checked) {
        updated.category = false;
        updated.subCategory = false;
      }
      if (name === "category" && !checked) {
        updated.subCategory = false;
      }

      return updated;
    });
  };

  const handleSubmit = async () => {
    let isValid = await trigger();
    if (isValid) {
      console.log(isValid, errors, formFields);

      let payload = { ...formFields };

      // Cleanup payload based on tenant
      if (
        (selectedTenant === "axis" || selectedTenant === "hc") &&
        formFields.registration_type
      ) {
        payload.bin_numbers = formFields.bin_numbers.filter(
          (bin) => bin.from && bin.to,
        );
      } else {
        delete payload.registration_type;
        delete payload.bin_numbers;
      }

      console.log(payload, "CREATE PALN");

      dispatch(
        createProgramNew({ ...payload, syncWithPlCrm, pagination: pagination }),
      ).then((result) => {
        if (result.type === "programData/createProgramNew/fulfilled") {
          toast.success(
            syncWithPlCrm
              ? "Program created and synced to PL-CRM."
              : "Program created successfully",
          );
          toggle();
        } else if (result.type === "programData/createProgramNew/rejected") {
          // Backend aborted (e.g. PL-CRM sync failed) — show it and keep the
          // modal open to retry; nothing was created on either side.
          const msg = result.error?.message || "Failed to create program";
          toast.error(
            syncWithPlCrm
              ? `PL-CRM sync failed — program not created. (${msg})`
              : msg,
          );
        }
      });
    }
  };

  const registrationTypeOptions = [
    { label: "Manual Reg Cards Numbers", value: "Manual Reg Cards Numbers" },
    { label: "Card Number", value: "Card Number" },
  ];

  const handleBinNumberChange = (index, field, value) => {
    const updatedBinNumbers = [...formFields.bin_numbers];
    updatedBinNumbers[index][field] = value;
    setFormFields((prev) => ({
      ...prev,
      bin_numbers: updatedBinNumbers,
    }));
  };

  const addBinNumber = () => {
    setFormFields((prev) => ({
      ...prev,
      bin_numbers: [...prev.bin_numbers, { from: "", to: "" }],
    }));
  };

  const removeBinNumber = (index) => {
    const updatedBinNumbers = formFields.bin_numbers.filter(
      (_, i) => i !== index,
    );
    setFormFields((prev) => ({
      ...prev,
      bin_numbers:
        updatedBinNumbers.length > 0
          ? updatedBinNumbers
          : [{ from: "", to: "" }],
    }));
  };

  // const selectedClientOption = clientOptions.find(
  //   (option) => option.value === formFields.client
  // );

  const handleInput = (eventOrOption, field) => {
    const value =
      eventOrOption?.target?.value !== undefined
        ? eventOrOption.target.value
        : eventOrOption?.value || "";

    setValue(field, value);
    setFormFields((prev) => {
      const next = { ...prev, [field]: value };
      // Keep slug_name auto-derived from the Program Name (matching
      // pl-server's own sanitization exactly) until the admin edits it
      // directly - program_slug is what every customer upload flow
      // requires, so it needs to be visible and predictable up front,
      // not a hidden auto-generated value discovered only after saving.
      if (field === "name" && !slugManuallyEdited.current) {
        next.slug_name = value
          .replace(/[^a-zA-Z0-9]/g, "_")
          .replace(/_+/g, "_");
        setValue("slug_name", next.slug_name);
      }
      return next;
    });
  };

  const handleSlugInput = (event) => {
    slugManuallyEdited.current = true;
    handleInput(event, "slug_name");
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop="static">
      <ModalHeader>Create Program</ModalHeader>
      <ModalBody>
        <div className="px-2">
          <div>
            <Label>
              Program Name<span className="text-danger">*</span>
            </Label>
            <Input
              name="name"
              type="text"
              placeholder="Enter program name"
              value={formFields.name}
              {...formFieldsValidation.name}
              onChange={(event) => handleInput(event, "name")}
              required
            />
            {errors.name && (
              <span className="mt-1 text-danger">{errors.name.message}</span>
            )}
          </div>

          <div className="mt-2">
            <Label>Program Slug</Label>
            <Input
              name="slug_name"
              type="text"
              placeholder="Auto-generated from Program Name"
              value={formFields.slug_name}
              onChange={handleSlugInput}
            />
            <small className="text-muted">
              Used as the <code>program_slug</code> value when uploading
              customers (Direct Customer Upload, bulk-upload CSVs). Auto-filled
              from the Program Name; edit it here if you need a specific value.
            </small>
          </div>

          <div className="mt-2">
            <Label>Select Country</Label>
            <select
              className="form-control mb-2"
              name="country"
              value={formFields.country}
              onChange={(e) => handleInput(e, "country")}
            >
              <option value="">Select Country</option>
              {Array.isArray(countries) &&
                countries.map((c) => (
                  <option key={c.iso3 || c.code || c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
            </select>

            <Label>Select Client</Label>
            <Select
              options={clientOptions}
              isClearable={true}
              name="client_id"
              placeholder="Select client"
              value={
                clientOptions.find((o) => o.value === formFields.client_id) ||
                null
              }
              onChange={(option) => handleInput(option, "client_id")}
            />
            <small className="text-muted">
              Every customer assigned to this Program (via{" "}
              <code>program_slug</code>) belongs to this Client - set at
              customer-creation time.
            </small>

            <Label className="mt-2">Select Membership Plan</Label>
            <Select
              options={membershipOptions}
              isClearable={true}
              name="membership_plan"
              placeholder="Select membership plan"
              value={
                membershipOptions.find(
                  (o) => o.value === formFields.membership_plan,
                ) || null
              }
              onChange={(option) => handleInput(option, "membership_plan")}
            />
          </div>

          {/* --- New Hierarchy Selection --- */}
          <div className="mt-3 border p-3 rounded">
            <Label className="fw-bold mb-2">Program Configuration</Label>

            <FormGroup check>
              <Input
                type="radio"
                name="plan"
                checked={formFields.plan}
                // Plan is default true, so we prevent unchecking it manually or keep it disabled as checked
                disabled={true}
                onChange={handleHierarchyChange}
              />
              <Label check>Plan (Default)</Label>
            </FormGroup>

            <FormGroup check className="ms-3 mt-2">
              <Input
                type="radio"
                name="service"
                checked={formFields.service}
                // Enable only if Plan is true
                disabled={!formFields.plan}
                onClick={(e) => {
                  // Allow toggling off for radio buttons by handling click manually if needed,
                  // or better yet, use checkboxes for toggles.
                  // If strictly 'radio' UI is needed but toggle behavior:
                  if (formFields.service) {
                    e.preventDefault();
                    handleHierarchyChange({
                      target: { name: "service", checked: false },
                    });
                  }
                }}
                onChange={handleHierarchyChange}
              />
              <Label check>Service</Label>
            </FormGroup>

            <FormGroup check className="ms-4 mt-2">
              <Input
                type="radio"
                name="subService"
                checked={formFields.subService}
                // Enable only if Service is true
                disabled={!formFields.service}
                onClick={(e) => {
                  if (formFields.subService) {
                    e.preventDefault();
                    handleHierarchyChange({
                      target: { name: "subService", checked: false },
                    });
                  }
                }}
                onChange={handleHierarchyChange}
              />
              <Label check>Subservice</Label>
            </FormGroup>

            <FormGroup check className="ms-5 mt-2">
              <Input
                type="radio"
                name="category"
                checked={formFields.category}
                // Enable only if Subservice is true
                disabled={!formFields.subService}
                onClick={(e) => {
                  if (formFields.category) {
                    e.preventDefault();
                    handleHierarchyChange({
                      target: { name: "category", checked: false },
                    });
                  }
                }}
                onChange={handleHierarchyChange}
              />
              <Label check>Category</Label>
            </FormGroup>

            <FormGroup check className="ms-5 ps-4 mt-2">
              <Input
                type="radio"
                name="subCategory"
                checked={formFields.subCategory}
                // Enable only if Category is true
                disabled={!formFields.category}
                onClick={(e) => {
                  if (formFields.subCategory) {
                    e.preventDefault();
                    handleHierarchyChange({
                      target: { name: "subCategory", checked: false },
                    });
                  }
                }}
                onChange={handleHierarchyChange}
              />
              <Label check>Subcategory</Label>
            </FormGroup>
          </div>

          <div className="mt-2">
            <Label>
              Program Description<span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              placeholder="Enter program description"
              {...formFieldsValidation.description}
              onChange={(event) => handleInput(event, "description")}
              required
            />
            {errors.description && (
              <span className="mt-1 text-danger">
                {errors.description.message}
              </span>
            )}
          </div>
          <div className="mt-2">
            <MbForm
              value={formFields.mb_form}
              formFields={formFields}
              handleInput={handleInput}
              onChange={(data) =>
                setFormFields((prev) => ({
                  ...prev,
                  mb_form: data,
                }))
              }
            />
          </div>

          {(selectedTenant === "axis" || selectedTenant === "hc") && (
            <>
              <div className="mt-2">
                <Label>Select Registration Type</Label>
                <Select
                  options={registrationTypeOptions}
                  isClearable={true}
                  placeholder="Select registration type"
                  value={registrationTypeOptions.find(
                    (opt) => opt.value === formFields.registration_type,
                  )}
                  onChange={(option) => {
                    setFormFields((prev) => ({
                      ...prev,
                      registration_type: option?.value || "",
                    }));
                  }}
                />
              </div>

              {formFields.registration_type && (
                <div className="mt-2">
                  <Label>Enter Bin Number:</Label>
                  {formFields.bin_numbers.map((bin, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-center gap-2 mt-2"
                    >
                      <div className="flex-fill">
                        <Input
                          type="text"
                          placeholder="From"
                          value={bin.from}
                          onChange={(e) =>
                            handleBinNumberChange(index, "from", e.target.value)
                          }
                        />
                      </div>
                      <div className="flex-fill">
                        <Input
                          type="text"
                          placeholder="To"
                          value={bin.to}
                          onChange={(e) =>
                            handleBinNumberChange(index, "to", e.target.value)
                          }
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-success"
                        onClick={addBinNumber}
                        title="Add bin number"
                      >
                        +
                      </button>
                      {formFields.bin_numbers.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => removeBinNumber(index)}
                          title="Remove bin number"
                        >
                          X
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Additional Details (accordion) */}
          <div
            className="mt-4"
            style={{
              border: "1px solid #dee2e6",
              borderRadius: 8,
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{
                background: "#e9ecef",
                borderBottom: detailsOpen ? "1px solid #ced4da" : "none",
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() => setDetailsOpen((o) => !o)}
            >
              <div className="d-flex align-items-center gap-2">
                <Label
                  className="mb-0 fw-semibold"
                  style={{ cursor: "pointer", fontSize: 14 }}
                >
                  Add Details
                </Label>
              </div>
              <span style={{ fontSize: 12, color: "#6c757d" }}>
                {detailsOpen ? "▲" : "▼"}
              </span>
            </div>

            {detailsOpen && (
              <div style={{ padding: "14px 16px" }}>
                <div className="d-flex gap-2 mb-3">
                  <Input
                    type="text"
                    placeholder="Enter detail title…"
                    value={newDetailText}
                    onChange={(e) => setNewDetailText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addDetail();
                      }
                    }}
                    style={{ fontSize: 13, borderRadius: 6 }}
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-sm px-3"
                    style={{ whiteSpace: "nowrap", borderRadius: 6 }}
                    onClick={addDetail}
                  >
                    + Add
                  </button>
                </div>
                <div className="d-flex flex-column gap-3">
                  {formFields.additional_details.map((detail, index) => (
                    <div
                      key={index}
                      style={{
                        border: "1px solid #ced4da",
                        borderRadius: 8,
                        background: "#f8f9fa",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        className="d-flex justify-content-between align-items-center px-3 py-2"
                        style={{
                          background: "#e9ecef",
                          borderBottom: "1px solid #ced4da",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#343a40",
                          }}
                        >
                          {index + 1}.&nbsp;{detail.text}
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          style={{
                            fontSize: 11,
                            padding: "2px 8px",
                            borderRadius: 4,
                          }}
                          onClick={() => removeDetail(index)}
                        >
                          Remove
                        </button>
                      </div>
                      <div className="px-3 py-2">
                        <div className="d-flex gap-2 mb-2">
                          <Input
                            type="text"
                            placeholder="Add sub-detail…"
                            value={subDetailInputs[index] || ""}
                            onChange={(e) =>
                              setSubDetailInputs((prev) => ({
                                ...prev,
                                [index]: e.target.value,
                              }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addSubDetail(index);
                              }
                            }}
                            style={{
                              fontSize: 13,
                              borderRadius: 6,
                              background: "#fff",
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            style={{
                              whiteSpace: "nowrap",
                              fontSize: 12,
                              borderRadius: 6,
                            }}
                            onClick={() => addSubDetail(index)}
                          >
                            + Add
                          </button>
                        </div>
                        {(detail.subDetails || []).length > 0 && (
                          <div className="d-flex flex-column gap-1 mt-1">
                            {detail.subDetails.map((sub, si) => (
                              <div
                                key={si}
                                className="d-flex align-items-center justify-content-between"
                                style={{
                                  background: "#fff",
                                  border: "1px solid #dee2e6",
                                  borderRadius: 6,
                                  padding: "5px 10px",
                                  fontSize: 12,
                                }}
                              >
                                <span style={{ color: "#495057" }}>
                                  {sub.text}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeSubDetail(index, si)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    cursor: "pointer",
                                    color: "#dc3545",
                                    fontSize: 16,
                                    lineHeight: 1,
                                  }}
                                  title="Remove"
                                >
                                  &times;
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Add Program Details */}
          <div
            className="mt-4"
            style={{
              border: "1px solid #dee2e6",
              borderRadius: 8,
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2"
              style={{
                background: "#e9ecef",
                borderBottom: programDetailsOpen ? "1px solid #ced4da" : "none",
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() => setProgramDetailsOpen((o) => !o)}
            >
              <div className="d-flex align-items-center gap-2">
                <Label
                  className="mb-0 fw-semibold"
                  style={{ cursor: "pointer", fontSize: 14 }}
                >
                  Add Program Details
                </Label>
              </div>
              <span style={{ fontSize: 12, color: "#6c757d" }}>
                {programDetailsOpen ? "▲" : "▼"}
              </span>
            </div>

            {programDetailsOpen && (
              <div style={{ padding: "14px 16px" }}>
                <div className="d-flex gap-2 mb-3">
                  <Input
                    type="text"
                    placeholder="Enter program detail title…"
                    value={newProgramDetailText}
                    onChange={(e) => setNewProgramDetailText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addProgramDetail();
                      }
                    }}
                    style={{ fontSize: 13, borderRadius: 6 }}
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-sm px-3"
                    style={{ whiteSpace: "nowrap", borderRadius: 6 }}
                    onClick={addProgramDetail}
                  >
                    + Add
                  </button>
                </div>
                <div className="d-flex flex-column gap-3">
                  {(formFields.program_details || []).map((detail, index) => (
                    <div
                      key={index}
                      style={{
                        border: "1px solid #ced4da",
                        borderRadius: 8,
                        background: "#f8f9fa",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        className="d-flex justify-content-between align-items-center px-3 py-2"
                        style={{
                          background: "#e9ecef",
                          borderBottom: "1px solid #ced4da",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#343a40",
                          }}
                        >
                          {index + 1}.&nbsp;{detail.text}
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          style={{
                            fontSize: 11,
                            padding: "2px 8px",
                            borderRadius: 4,
                          }}
                          onClick={() => removeProgramDetail(index)}
                        >
                          Remove
                        </button>
                      </div>
                      <div className="px-3 py-2">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <label
                            htmlFor={`prog-detail-img-${index}`}
                            className="btn btn-sm btn-outline-secondary mb-0"
                            style={{
                              fontSize: 12,
                              borderRadius: 6,
                              cursor: "pointer",
                            }}
                          >
                            {programDetailUploading[index]
                              ? "Uploading…"
                              : detail.image_url
                                ? "Change Image"
                                : "Upload Image"}
                          </label>
                          <input
                            id={`prog-detail-img-${index}`}
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) uploadProgramDetailImage(index, file);
                              e.target.value = "";
                            }}
                          />
                          {detail.image_url && (
                            <>
                              <img
                                src={`${process.env.REACT_APP_PL_API}/v1/media/media/${detail.image_url}`}
                                alt="detail"
                                style={{
                                  width: 40,
                                  height: 40,
                                  objectFit: "cover",
                                  borderRadius: 4,
                                  border: "1px solid #ced4da",
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => removeProgramDetailImage(index)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  cursor: "pointer",
                                  color: "#dc3545",
                                  fontSize: 12,
                                }}
                                title="Remove image"
                              >
                                Remove image
                              </button>
                            </>
                          )}
                        </div>
                        <div className="d-flex gap-2 mb-2">
                          <Input
                            type="text"
                            placeholder="Add sub-detail…"
                            value={programSubDetailInputs[index] || ""}
                            onChange={(e) =>
                              setProgramSubDetailInputs((prev) => ({
                                ...prev,
                                [index]: e.target.value,
                              }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addProgramSubDetail(index);
                              }
                            }}
                            style={{
                              fontSize: 13,
                              borderRadius: 6,
                              background: "#fff",
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            style={{
                              whiteSpace: "nowrap",
                              fontSize: 12,
                              borderRadius: 6,
                            }}
                            onClick={() => addProgramSubDetail(index)}
                          >
                            + Add
                          </button>
                        </div>
                        {(detail.subDetails || []).length > 0 && (
                          <div className="d-flex flex-column gap-1 mt-1">
                            {detail.subDetails.map((sub, si) => (
                              <div
                                key={si}
                                className="d-flex align-items-center justify-content-between"
                                style={{
                                  background: "#fff",
                                  border: "1px solid #dee2e6",
                                  borderRadius: 6,
                                  padding: "5px 10px",
                                  fontSize: 12,
                                }}
                              >
                                <span style={{ color: "#495057" }}>
                                  {sub.text}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeProgramSubDetail(index, si)
                                  }
                                  style={{
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    cursor: "pointer",
                                    color: "#dc3545",
                                    fontSize: 16,
                                    lineHeight: 1,
                                  }}
                                  title="Remove"
                                >
                                  &times;
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add-on Service List */}
            <div
              className="mt-4"
              style={{
                border: "1px solid #dee2e6",
                borderRadius: 8,
                padding: "14px 16px",
                background: "#fff",
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="fw-semibold" style={{ fontSize: 14 }}>
                  Add-on Service List
                </span>
                <div className="d-flex align-items-center gap-2">
                  {addOnOptions.length > 0 && (
                    <div style={{ minWidth: 220 }}>
                      <Select
                        options={addOnOptions}
                        value={selectedMaster}
                        onChange={(val) => setSelectedMaster(val)}
                        isClearable={false}
                        placeholder="Select master"
                        styles={{
                          container: (base) => ({ ...base, minWidth: 220 }),
                        }}
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary btn-sm px-3"
                    style={{ borderRadius: 6, fontSize: 12 }}
                    onClick={addAddon}
                  >
                    + Add Row
                  </button>
                </div>
              </div>
              {formFields.add_on_services.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <table
                    className="table table-bordered table-sm mb-0"
                    style={{ fontSize: 12, minWidth: 600 }}
                  >
                    <thead style={{ background: "#e9ecef" }}>
                      <tr>
                        <th style={{ width: 36, textAlign: "center" }}>
                          Enable
                        </th>
                        <th>Limits</th>
                        <th>Default Free</th>
                        <th>Extra Free</th>
                        <th>Purchase Slot</th>
                        <th>Total Slot</th>
                        <th style={{ width: 60, textAlign: "center" }}>
                          Remove
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {formFields.add_on_services.map((row, index) => (
                        <tr
                          key={index}
                          style={{
                            verticalAlign: "middle",
                            opacity: row.isEnabled ? 1 : 0.5,
                          }}
                        >
                          <td style={{ textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={!!row.isEnabled}
                              onChange={() =>
                                updateAddon(index, "isEnabled", !row.isEnabled)
                              }
                              style={{
                                width: 15,
                                height: 15,
                                cursor: "pointer",
                                marginTop: 0,
                                opacity: 1,
                                position: "relative",
                                zIndex: 2,
                                WebkitAppearance: "checkbox",
                                MozAppearance: "checkbox",
                                appearance: "checkbox",
                                background: "transparent",
                              }}
                              title={row.isEnabled ? "Enabled" : "Disabled"}
                            />
                          </td>
                          <td>
                            <span style={{ fontSize: 12 }}>
                              {row.limits || "—"}
                            </span>
                          </td>
                          {[
                            "default_free",
                            "extraFree",
                            "purchase_slot",
                            "total_slot",
                          ].map((field) => (
                            <td key={field}>
                              <Input
                                type="text"
                                value={row[field]}
                                onChange={(e) =>
                                  updateAddon(index, field, e.target.value)
                                }
                                style={{
                                  fontSize: 12,
                                  padding: "3px 6px",
                                  border: "none",
                                  boxShadow: "none",
                                  background: "transparent",
                                }}
                                placeholder="—"
                              />
                            </td>
                          ))}
                          <td style={{ textAlign: "center" }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              style={{
                                fontSize: 11,
                                padding: "2px 8px",
                                borderRadius: 4,
                              }}
                              onClick={() => removeAddon(index)}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                  No add-on services added yet.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Sync with PL-CRM Toggle */}
        <div className="px-2 mt-3 mb-2">
          <div
            style={{
              background: "linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%)",
              borderRadius: 10,
              border: "1.5px solid #c3d4f5",
              padding: "12px 16px",
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div
                className="d-flex flex-column"
                style={{ flex: 1, paddingRight: 16 }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    color: "#1a3a6b",
                    marginBottom: 4,
                  }}
                >
                  Sync with PL-CRM
                </span>
                <small
                  style={{
                    color: syncWithPlCrm ? "#1a6b3a" : "#6b7280",
                    lineHeight: 1.5,
                  }}
                >
                  {syncWithPlCrm ? (
                    <>
                      <span style={{ color: "#16a34a", fontWeight: 600 }}>
                        ✓ Sync enabled
                      </span>{" "}
                      — This program will also be created in{" "}
                      <strong>PL-CRM Europassistance</strong>.
                    </>
                  ) : (
                    <>
                      Once you enable this, the same program will be generated
                      in the <strong>PL-CRM Europassistance</strong> system.
                    </>
                  )}
                </small>
              </div>
              {/* Custom Toggle Switch */}
              <div
                onClick={() => setSyncWithPlCrm(!syncWithPlCrm)}
                id="syncWithPlCrmProgramToggle"
                title={syncWithPlCrm ? "Disable sync" : "Enable sync"}
                style={{
                  width: 52,
                  height: 28,
                  borderRadius: 28,
                  background: syncWithPlCrm ? "#22c55e" : "#cbd5e1",
                  border: syncWithPlCrm
                    ? "2px solid #16a34a"
                    : "2px solid #94a3b8",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 3px",
                  cursor: "pointer",
                  transition: "background 0.25s, border-color 0.25s",
                  flexShrink: 0,
                  boxShadow: syncWithPlCrm
                    ? "0 0 0 3px rgba(34,197,94,0.2)"
                    : "0 0 0 2px rgba(148,163,184,0.2)",
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: "#ffffff",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                    transform: syncWithPlCrm
                      ? "translateX(22px)"
                      : "translateX(0px)",
                    transition: "transform 0.25s",
                    flexShrink: 0,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter className="d-flex customModalFooter">
        <button className="btn bg-primary" onClick={() => handleSubmit()}>
          Create
        </button>
        <button className="btn bg-danger" onClick={() => toggle()}>
          Cancel
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default CreateProgramModal;
